import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { ShieldCheck, Loader2 } from "lucide-react";

export default function AdminLogin() {
  const [step, setStep] = useState("password"); // "password" | "2fa"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [useBackup, setUseBackup] = useState(false);
  const [pendingToken, setPendingToken] = useState("");
  const [setupMode, setSetupMode] = useState(false);
  const [qr, setQr] = useState("");
  const [secret, setSecret] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) {
        localStorage.setItem("cl_admin_token", data.access_token);
        navigate("/admin");
        return;
      }
      setPendingToken(data.pending_token);
      if (data.status === "2fa_setup_required") {
        setSetupMode(true);
        setQr(data.qr || "");
        setSecret(data.secret || "");
      } else {
        setSetupMode(false);
      }
      setStep("2fa");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/2fa/verify", { pending_token: pendingToken, code });
      localStorage.setItem("cl_admin_token", data.access_token);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const restart = () => {
    setStep("password");
    setCode("");
    setError("");
    setSetupMode(false);
  };

  return (
    <div className="min-h-screen bg-[#070B1C] flex items-center justify-center px-5 py-20">
      <div className="w-full max-w-md border border-white/15 bg-[#0B1024] p-8 lg:p-12 sq-xl">
        <div className="font-display font-bold text-2xl tracking-tighter uppercase mb-1 text-white">Century Labs</div>
        <p className="text-[10px] tracking-[0.3em] uppercase text-electric mb-8">Admin Console</p>

        {step === "password" && (
          <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase font-bold block mb-2 text-white/70">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="cl-input"
                data-testid="admin-email"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase font-bold block mb-2 text-white/70">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="cl-input"
                data-testid="admin-password"
              />
            </div>
            {error && <p className="text-xs text-electric" data-testid="admin-login-error">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-4 text-xs tracking-[0.2em] uppercase font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2"
              data-testid="admin-login-submit"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : null}
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        )}

        {step === "2fa" && (
          <form onSubmit={verify} className="space-y-5" data-testid="admin-2fa-form">
            <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase font-bold text-electric">
              <ShieldCheck size={14} /> Two-factor authentication
            </div>

            {setupMode ? (
              <div className="space-y-3" data-testid="admin-2fa-setup">
                <p className="text-sm text-white/60 leading-relaxed">
                  Set up 2FA once: open an authenticator app (Google Authenticator, Authy, 1Password)
                  and scan this QR code, then enter the 6-digit code it shows.
                </p>
                {qr && (
                  <div className="flex justify-center">
                    <img src={qr} alt="2FA QR code" className="w-44 h-44 bg-white p-2 sq-md" data-testid="admin-2fa-qr" />
                  </div>
                )}
                {secret && (
                  <div className="text-center">
                    <p className="text-[10px] tracking-[0.2em] uppercase text-white/40 mb-1">Can't scan? Enter this key</p>
                    <code className="text-xs text-white/80 break-all" data-testid="admin-2fa-secret">{secret}</code>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-white/60 leading-relaxed">
                {useBackup
                  ? "Enter your backup recovery code (the one you saved on paper)."
                  : "Enter the 6-digit code from your authenticator app."}
              </p>
            )}

            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase font-bold block mb-2 text-white/70">
                {useBackup ? "Backup recovery code" : "Authentication code"}
              </label>
              <input
                type="text"
                inputMode={useBackup ? "text" : "numeric"}
                autoComplete="one-time-code"
                required
                maxLength={useBackup ? 19 : 7}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={useBackup ? "XXXX-XXXX-XXXX" : "123456"}
                className={`cl-input text-center text-lg ${useBackup ? "tracking-[0.15em] uppercase" : "tracking-[0.4em]"}`}
                data-testid="admin-2fa-code"
                autoFocus
              />
            </div>

            {!setupMode && (
              <button
                type="button"
                onClick={() => { setUseBackup((v) => !v); setCode(""); setError(""); }}
                className="w-full text-[10px] tracking-[0.15em] uppercase text-electric/80 hover:text-electric transition-colors duration-200"
                data-testid="admin-2fa-toggle-backup"
              >
                {useBackup ? "← Use authenticator code" : "Lost your phone? Use a backup code"}
              </button>
            )}

            {error && <p className="text-xs text-electric" data-testid="admin-2fa-error">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-4 text-xs tracking-[0.2em] uppercase font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2"
              data-testid="admin-2fa-submit"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : null}
              {loading ? "Verifying..." : setupMode ? "Verify & Enable 2FA" : "Verify"}
            </button>
            <button
              type="button"
              onClick={restart}
              className="w-full text-[10px] tracking-[0.2em] uppercase text-white/40 hover:text-white/70 transition-colors duration-200"
              data-testid="admin-2fa-back"
            >
              ← Back to sign in
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
